import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, RefreshCw, User, Eye, Download, AlertTriangle, X, CheckCircle, List } from 'lucide-react';
import MainService from '../../services/MainService';
import * as XLSX from 'xlsx';
import ClientListGrid, { useClientListGrid } from '../ui/ClientListGrid';

const LIST_TAKE = 10000;

const CUSTOMER_COLUMNS = [
  { field: 'voucherno', title: 'Voucher No', widthClass: 'w-[10%]', hideable: true },
  { field: 'clientbill', title: 'Client Bill', widthClass: 'w-[10%]', hideable: true },
  { field: 'client', title: 'Client', widthClass: 'w-[12%]', hideable: true },
  { field: 'customer', title: 'Customer', widthClass: 'w-[12%]', hideable: true },
  { field: 'status', title: 'Status', widthClass: 'w-[8%]', hideable: true },
  { field: 'address', title: 'Address', widthClass: 'w-[12%]', hideable: true },
  { field: 'location', title: 'Location', widthClass: 'w-[8%]', hideable: true },
  { field: 'emirate', title: 'Emirate', widthClass: 'w-[8%]', hideable: true },
  { field: 'orderdate', title: 'Order Date', widthClass: 'w-[8%]', hideable: true },
  { field: 'deliverydate', title: 'Delivery Date', widthClass: 'w-[8%]', hideable: true },
  { field: 'collectionamount', title: 'Collection Amount', widthClass: 'w-[10%]', hideable: true, align: 'right' },
  { field: 'clientamount', title: 'Client Amount', widthClass: 'w-[10%]', hideable: true, align: 'right' },
  { field: 'actions', title: 'Actions', widthClass: 'w-[6%]', hideable: true, sortable: false, filterable: false, groupable: false, align: 'center' },
];

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-GB');
};

const formatAmount = (amount) => {
  if (amount != null && amount !== '') return `AED ${parseFloat(amount).toFixed(2)}`;
  return 'N/A';
};

const getCellValue = (row, field) => {
  switch (field) {
    case 'voucherno':
      return row.VoucherNo || 'N/A';
    case 'clientbill':
      return row.ClientBillNo || 'N/A';
    case 'client':
      return row.Client?.Name || 'N/A';
    case 'customer':
      return row.Customer?.Name || 'N/A';
    case 'status':
      return row.Status?.Name || 'N/A';
    case 'address':
      return row.CustomerAddress?.Address || 'N/A';
    case 'location':
      return row.CustomerAddress?.Location?.Name || 'N/A';
    case 'emirate':
      return row.CustomerAddress?.Emirate?.Name || 'N/A';
    case 'orderdate':
      return formatDate(row.Date);
    case 'deliverydate':
      return formatDate(row.DeliveryDate);
    case 'collectionamount':
      return formatAmount(row.CustomerAmount);
    case 'clientamount':
      return formatAmount(row.ClientAmount);
    default:
      return row[field];
  }
};

const NestedName = ({ name, mobile }) => (
  <div>
    <div className="font-medium text-gray-900">{name || 'N/A'}</div>
    <div className="text-xs text-gray-500">{mobile || 'N/A'}</div>
  </div>
);

const TrackByCustomer = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [warning, setWarning] = useState('');

  const columns = useMemo(() => CUSTOMER_COLUMNS, []);
  const grid = useClientListGrid({ columns, rows: orders, getCellValue });

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (warning) {
      const timer = setTimeout(() => {
        setWarning('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [warning]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setWarning('Please enter customer name or mobile number');
      return;
    }

    try {
      setHasSearched(true);
      setIsLoading(true);
      setError('');
      setSuccess('');
      setWarning('');

      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN') || localStorage.getItem('userInfo');
      if (!tokenData) {
        setError('Please login first');
        return;
      }

      const userData = typeof tokenData === 'string' ? JSON.parse(tokenData) : tokenData;

      const data = {
        Keyword: searchQuery.trim(),
        Skip: 0,
        Take: LIST_TAKE,
      };

      const token = userData.Token || localStorage.getItem('authToken');
      const response = await MainService.TrackOrdersByCustomerInPortal(data, token);

      if (response && response.Orders && response.Orders.length > 0) {
        setOrders(response.Orders);
        setTotalCount(response.TotalCount || response.Orders.length);
        setSuccess(`Found ${response.TotalCount || response.Orders.length} order(s) for "${searchQuery}"`);
      } else {
        setOrders([]);
        setTotalCount(0);
        setWarning(`No orders found for "${searchQuery}"`);
      }
    } catch (err) {
      console.error('Error searching orders:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setOrders([]);
    setHasSearched(false);
    setTotalCount(0);
    setError('');
    setSuccess('');
    setWarning('');
  };

  const exportToExcel = () => {
    if (orders.length === 0) {
      setWarning('No orders to export');
      return;
    }

    try {
      const exportData = grid.processedRows.map((order, index) => ({
        '#': index + 1,
        'Voucher No': order.VoucherNo || 'N/A',
        'Client Bill No': order.ClientBillNo || 'N/A',
        'Customer Name': order.Customer?.Name || 'N/A',
        'Mobile No1': order.Customer?.MobileNo1 || 'N/A',
        'Mobile No2': order.Customer?.MobileNo2 || 'N/A',
        'Status': order.Status?.Name || 'N/A',
        'Address': order.CustomerAddress?.Address || 'N/A',
        'Location': order.CustomerAddress?.Location?.Name || 'N/A',
        'Emirate': order.CustomerAddress?.Emirate?.Name || 'N/A',
        'Order Date': order.Date ? new Date(order.Date).toLocaleDateString('en-GB') : 'N/A',
        'Pickup Date': order.PickupDate ? new Date(order.PickupDate).toLocaleDateString('en-GB') : 'N/A',
        'Delivery Date': order.DeliveryDate ? new Date(order.DeliveryDate).toLocaleDateString('en-GB') : 'N/A',
        'Product Price': order.ProductPrice ? `AED ${parseFloat(order.ProductPrice).toFixed(2)}` : 'N/A',
        'Delivery Charge': order.DeliveryCharge ? `AED ${parseFloat(order.DeliveryCharge).toFixed(2)}` : 'N/A',
        'Tax': order.TAX ? `AED ${parseFloat(order.TAX).toFixed(2)}` : 'N/A',
        'Collection Amount': order.CustomerAmount ? `AED ${parseFloat(order.CustomerAmount).toFixed(2)}` : 'N/A',
        'Client Amount': order.ClientAmount ? `AED ${parseFloat(order.ClientAmount).toFixed(2)}` : 'N/A',
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Search Results');
      const fileName = `search_results_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setSuccess('Excel file exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export to Excel');
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {warning && (
        <div className="fixed top-20 right-4 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{warning}</span>
          <button onClick={() => setWarning('')} className="text-yellow-500 hover:text-yellow-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center mb-2">
            <User className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">Track By Customer</h1>
          </div>
          <p className="text-sm text-gray-500 ml-8">Search orders by customer name or mobile number</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Search
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter customer name or mobile number"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="flex items-end gap-3">
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="bg-primary hover:bg-primary/90 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
            >
              <Search className="w-4 h-4" />
              <span>Search</span>
            </button>
            <button
              onClick={handleClear}
              className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Clear</span>
            </button>
          </div>
        </div>
      </div>

      {hasSearched ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center">
              <List className="w-5 h-5 text-primary mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">
                Search Results ({totalCount} orders found)
              </h3>
            </div>
            <button
              onClick={exportToExcel}
              className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors flex items-center gap-2"
              title="Export Excel"
            >
              <Download className="w-5 h-5" />
              <span className="hidden sm:inline">Export Excel</span>
            </button>
          </div>

          <ClientListGrid
            columns={columns}
            grid={grid}
            loading={isLoading}
            loadingText="Loading..."
            emptyText={`No orders found for "${searchQuery}"`}
            minWidth="1600px"
            rowKey={(row, index) => row.Id || index}
            renderCell={(row, column) => {
              if (column.field === 'voucherno') {
                return (
                  <span className="inline-flex items-center gap-2 font-medium">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    {row.VoucherNo || 'N/A'}
                  </span>
                );
              }
              if (column.field === 'client') {
                return <NestedName name={row.Client?.Name} mobile={row.Client?.MobileNo1} />;
              }
              if (column.field === 'customer') {
                return <NestedName name={row.Customer?.Name} mobile={row.Customer?.MobileNo1} />;
              }
              if (column.field === 'status') {
                return (
                  <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800">
                    {row.Status?.Name || 'N/A'}
                  </span>
                );
              }
              if (column.field === 'collectionamount' || column.field === 'clientamount') {
                return <span className="font-semibold text-gray-900">{getCellValue(row, column.field)}</span>;
              }
              if (column.field === 'actions') {
                return (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/home/orders/${row.Id}`);
                    }}
                    className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"
                    title="View Order Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                );
              }
              return getCellValue(row, column.field);
            }}
          />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-blue-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Search by Customer</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Enter customer name or mobile number to find their orders. You can search by partial names or phone numbers.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <div className="bg-gray-50 rounded-lg p-4 text-left">
                <h4 className="font-medium text-gray-900 mb-2">Search Examples:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Customer name: "John Doe"</li>
                  <li>• Partial name: "John" or "Doe"</li>
                  <li>• Mobile number: "0501234567"</li>
                  <li>• Partial mobile: "050123"</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-left">
                <h4 className="font-medium text-gray-900 mb-2">What you'll see:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• All orders for the customer</li>
                  <li>• Order status and dates</li>
                  <li>• Delivery addresses</li>
                  <li>• Payment information</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackByCustomer;
